<script lang="ts" setup>
import type { Addon } from '~/models';

const props = defineProps<{
  addon: Addon;
  productPrice: number;
  selectionType: boolean;
}>();

const emits = defineEmits<{
  (e: 'add'): void;
  (e: 'remove'): void;
}>();

const textPrice = computed<string>(() => {
  switch (props.addon.showPrice) {
    case 'Show as Extra': {
      return `+ ${useCurrency().format(props.addon.price)}`;
    }

    case 'Show as Total': {
      return useCurrency().format(props.productPrice + props.addon.price);
    }

    default: {
      return '';
    }
  }
});

const onAdd = (): void => emits('add');

const onRemove = (): void => emits('remove');
</script>

<template>
  <li
    class="addon-item items-center cursor-pointer border-t border-card rounded-none min-h-16 py-2 px-3"
    @click="onAdd"
  >
    <span
      class="addon-item__name"
      data-test="name"
    >
      {{ props.addon.name }}
    </span>

    <span
      v-if="props.addon.description"
      class="addon-item__description text-xs subtext-card"
      data-test="description"
    >
      {{ props.addon.description }}
    </span>

    <span class="addon-item__actions flex items-center gap-2 text-base">
      <span
        v-if="props.addon.price"
        class="text-company-1"
        data-test="price"
      >
        {{ textPrice }}
      </span>

      <template v-if="props.selectionType">
        <UIcon
          class="text-2xl text-company-1"
          :name="props.addon.selected
            ? 'ic:round-radio-button-checked'
            : 'ic:round-radio-button-unchecked'
            "
          data-test="selection"
          dynamic
        />
      </template>
      <template v-else>
        <UIcon
          v-if="props.addon.quantity > 0"
          class="text-2xl text-company-1"
          name="ic:round-remove-circle-outline"
          data-test="remove"
          @click.stop="onRemove"
        />

        <span
          v-if="props.addon.quantity"
          data-test="quantity"
        >
          {{ props.addon.quantity }}
        </span>

        <UIcon
          class="text-2xl text-company-1"
          name="ic:round-add-circle-outline"
          data-test="add"
        />
      </template>
    </span>
  </li>
</template>

<style lang="scss" scoped>
.addon-item {
  display: grid;
  grid-template-rows: 1fr auto;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'name action'
    'desc action';
  column-gap: 0.25rem;

  &__name {
    grid-area: name;
  }

  &__description {
    grid-area: desc;
  }

  &__actions {
    grid-area: action;
  }
}
</style>
